/* eslint-disable no-use-before-define */
import { Box, Divider, Drawer, Hidden, Link, List, ListSubheader } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { matchPath, Link as RouterLink, useLocation } from 'react-router-dom';
import Logo from 'src/components/Logo';
import NavbarLogo from 'src/components/NavbarLogo';
import { GAME, THEMES } from 'src/constants';
import { PATHS_APP_OVERVIEW, PATHS_AUTH, ROOT_APP } from 'src/routes/paths';
import { DASHBOARD_NAV_CONFIG } from './NavConfig';
import NavItem from './NavItem';

const PREFIX = 'NavBar';

const classes = {
  mobileDrawer: `${PREFIX}-mobileDrawer`,
  desktopDrawer: `${PREFIX}-desktopDrawer`,
  avatar: `${PREFIX}-avatar`,
  logoLayout: `${PREFIX}-logoLayout`,
  logo: `${PREFIX}-logo`,
  logoBackground: `${PREFIX}-logoBackground`,
  nav: `${PREFIX}-nav`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mobileDrawer}`]: {
    width: 256,
  },

  [`& .${classes.desktopDrawer}`]: {
    width: 256,
    paddingTop: 64,
  },

  [`& .${classes.avatar}`]: {
    height: 70,
    cursor: 'pointer',
  },

  [`& .${classes.logoLayout}`]: {
    display: 'flex',
    justifyContent: 'center',
  },

  [`& .${classes.logo}`]: {
    height: 40,
  },

  [`& .${classes.logoBackground}`]: {
    ...(theme.name === THEMES.LIGHT && {
      boxShadow: 'none',
      backgroundColor: theme.palette.primary.main,
    }),
    ...(theme.name === THEMES.ONE_DARK && {
      backgroundColor: theme.palette.background.default,
    }),
  },

  [`& .${classes.nav}`]: {
    padding: '8px 16px',
  },
}));

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>{items.reduce((acc, item) => reduceChildRoutes({ acc, item, pathname, depth }), [])}</List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(
      {
        path: item.href,
      },
      pathname,
    );

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
        badge={item.badge}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>,
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
        badge={item.badge}
      />,
    );
  }

  return acc;
}

const NavBar = ({ onMobileClose, openMobile }) => {
  const location = useLocation();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center" className={classes.logoBackground}>
            <RouterLink to={PATHS_AUTH.ROOT}>
              <Logo className={classes.logo} height={40} />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center" alignItems="center">
            <RouterLink to={ROOT_APP} className={classes.logoLayout}>
              <NavbarLogo className={classes.avatar} />
            </RouterLink>
          </Box>
          <Box mt={1} textAlign="center">
            <Link component={RouterLink} to={PATHS_APP_OVERVIEW.ROOT} variant="h5" color="textPrimary" underline="none">
              {GAME.name}
            </Link>
          </Box>
        </Box>
        <Divider />
        <Box className={classes.nav}>
          {DASHBOARD_NAV_CONFIG.map(section => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname,
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <Root>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        <Drawer anchor="left" classes={{ paper: classes.desktopDrawer }} open variant="persistent">
          {content}
        </Drawer>
      </Hidden>
    </Root>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
